// Live-capture catalogue: what the cardiac arrest and critical care consoles
// offer, what each tap writes, and the detail forms used to finish entries
// later. Pure data (plus a few showIf rules) - no platform imports.
//
// KEEP IN SYNC: duplicated byte-for-byte at
//   client/src/pages/patientRecords/ePCR/liveCapture/catalogue.js
//   mobile/src/components/EPCR/liveCapture/catalogue.js
// The server mirrors the case-review triggers in
//   server/src/helpers/epcr/criticalCareReview.js
//
// Clinical content is a documentation aid, not a protocol: option lists and
// reminders describe what crews commonly need to record. Sources: Lockey et
// al. "Safer prehospital anaesthesia: updated guidelines" (Anaesthesia
// 2026, replaces the 2017 guidance), Utstein airway template (Sunde 2018),
// Utstein OHCA 2024, RCUK 2025 (ALS/NLS), FPHC/BBA burns consensus 2020,
// NICE NG217/NG133/NG39, BTF prehospital TBI 3rd ed., World SIVA sedation
// adverse-event tool. Physiological targets differ between these sources,
// so none are enforced - reminders stay neutral and services follow their
// own SOPs. Doses are never suggested except where a tenant configures them
// (quick drugs) or the crew picks a weight-based induction ratio.
//
// Entry keys are human-readable labels on purpose: every existing entry
// display (Treatment list, EPCRView, PDF, mobile cards) prints keys as
// labels, so live entries render everywhere with no extra work.

// ---------------------------------------------------------------------------
// Shared option lists
// ---------------------------------------------------------------------------

const YES_NO = ['Yes', 'No']
const SIDES = ['Left', 'Right', 'Bilateral']
const LIMBS = ['Left arm', 'Right arm', 'Left leg', 'Right leg', 'Junctional', 'Other']
const OUTCOME = ['Successful', 'Unsuccessful', 'Abandoned']

export const CORMACK_LEHANE = [
  { value: '1', label: 'Grade 1', description: 'Most of the glottis visible' },
  { value: '2a', label: 'Grade 2a', description: 'Partial view of the glottis' },
  { value: '2b', label: 'Grade 2b', description: 'Arytenoids or posterior cords only' },
  { value: '3a', label: 'Grade 3a', description: 'Epiglottis only - liftable' },
  { value: '3b', label: 'Grade 3b', description: 'Epiglottis only - adherent to pharynx' },
  { value: '4', label: 'Grade 4', description: 'No glottic structures visible' },
]

const RHYTHMS = ['VF', 'pVT', 'PEA', 'Asystole', 'Organised rhythm (check pulse)']

// Consoles a drug is suggested on when a tenant has not configured its own
// list (Site Configuration -> Patient Records -> Critical care). Doses are
// adult reference doses shown on the button - crews confirm what was given
// when they finish the entry.
export const DEFAULT_QUICK_DRUGS = [
  { name: 'Adrenaline', dose: '1mg', route: 'IV/IO', categories: ['cardiacArrest'] },
  { name: 'Amiodarone', dose: '300mg', route: 'IV/IO', categories: ['cardiacArrest'] },
  { name: 'Amiodarone', dose: '150mg', route: 'IV/IO', categories: ['cardiacArrest'] },
  { name: 'Calcium chloride 10%', dose: '10ml', route: 'IV/IO', categories: ['cardiacArrest', 'codeRed', 'majorTrauma', 'medical'] },
  { name: 'Sodium bicarbonate 8.4%', dose: '50ml', route: 'IV/IO', categories: ['cardiacArrest', 'medical'] },
  { name: 'Magnesium sulfate', dose: '2g', route: 'IV/IO', categories: ['cardiacArrest', 'obstetric', 'medical'] },
  { name: 'Lidocaine', dose: '100mg', route: 'IV/IO', categories: ['cardiacArrest'] },
  { name: 'Naloxone', dose: '400mcg', route: 'IV/IM', categories: ['cardiacArrest', 'medical'] },
  { name: 'Adrenaline infusion', dose: '', route: 'IV/IO', categories: ['cardiacArrest', 'medical', 'majorTrauma'] },
  { name: 'Tranexamic acid', dose: '2g', route: 'IV/IO', categories: ['majorTrauma', 'codeRed', 'obstetric'] },
  { name: 'Ketamine', dose: '', route: 'IV/IO', categories: ['majorTrauma', 'phea', 'sedation', 'burns', 'neuroTrauma'] },
  { name: 'Fentanyl', dose: '', route: 'IV/IO', categories: ['majorTrauma', 'phea', 'sedation', 'burns', 'neuroTrauma'] },
  { name: 'Rocuronium', dose: '', route: 'IV/IO', categories: ['phea', 'neuroTrauma'] },
  { name: 'Morphine', dose: '', route: 'IV/IO', categories: ['majorTrauma', 'burns'] },
  { name: 'Midazolam', dose: '', route: 'IV/IO', categories: ['phea', 'sedation', 'seizure'] },
  { name: 'Propofol', dose: '', route: 'IV', categories: ['phea', 'sedation'] },
  { name: 'Co-amoxiclav', dose: '1.2g', route: 'IV', categories: ['majorTrauma'] },
  { name: 'Cefuroxime', dose: '1.5g', route: 'IV', categories: ['majorTrauma'] },
  { name: 'Hypertonic saline', dose: '', route: 'IV/IO', categories: ['neuroTrauma'] },
  { name: 'Mannitol', dose: '', route: 'IV', categories: ['neuroTrauma'] },
  { name: 'Hydroxocobalamin', dose: '5g', route: 'IV', categories: ['burns'] },
  { name: 'Oxytocin', dose: '', route: 'IM/IV', categories: ['obstetric'] },
  { name: 'Syntometrine', dose: '1ml', route: 'IM', categories: ['obstetric'] },
  { name: 'Misoprostol', dose: '', route: 'PO/PR', categories: ['obstetric'] },
  { name: 'Midazolam (buccal)', dose: '', route: 'Buccal', categories: ['seizure'] },
  { name: 'Diazepam (rectal)', dose: '', route: 'PR', categories: ['seizure'] },
  { name: 'Lorazepam', dose: '', route: 'IV', categories: ['seizure'] },
  { name: 'Levetiracetam', dose: '', route: 'IV', categories: ['seizure'] },
  { name: 'Phenytoin', dose: '', route: 'IV', categories: ['seizure'] },
  { name: 'Sodium valproate', dose: '', route: 'IV', categories: ['seizure'] },
  { name: 'Phenobarbital', dose: '', route: 'IV', categories: ['seizure'] },
  { name: 'Glucose 10%', dose: '', route: 'IV', categories: ['seizure', 'medical', 'newborn'] },
  { name: 'Adrenaline (IM 1:1000)', dose: '500mcg', route: 'IM', categories: ['medical'] },
  { name: 'Adrenaline (neonatal)', dose: '', route: 'IV/IO', categories: ['newborn'] },
  { name: 'Metaraminol', dose: '', route: 'IV', categories: ['phea', 'medical', 'neuroTrauma'] },
]

// PHEA induction ratios: fentanyl mcg/kg : ketamine mg/kg : rocuronium mg/kg.
// Tenants' SOPs differ - the console only offers these when the crew picks
// one, and the computed doses are pre-filled for the crew to confirm.
// UK services vary (i-PHEA survey 2023): 3:2:1 (KSS), 1:2:1 and 0:1:1
// (GNAAS), 1:1:1 for compromised patients; rocuronium is often given at
// 1.2-2 mg/kg - the computed dose is only a starting value the crew confirms.
export const INDUCTION_RATIOS = [
  { value: '3:2:1', label: '3:2:1', description: 'Fentanyl 3 · Ketamine 2 · Roc 1', perKg: { Fentanyl: 3, Ketamine: 2, Rocuronium: 1 } },
  { value: '1:2:1', label: '1:2:1', description: 'Fentanyl 1 · Ketamine 2 · Roc 1', perKg: { Fentanyl: 1, Ketamine: 2, Rocuronium: 1 } },
  { value: '1:1:1', label: '1:1:1', description: 'Compromised', perKg: { Fentanyl: 1, Ketamine: 1, Rocuronium: 1 } },
  { value: '0:1:1', label: '0:1:1', description: 'Shocked / peri-arrest', perKg: { Fentanyl: 0, Ketamine: 1, Rocuronium: 1 } },
  { value: 'Other', label: 'Other', description: 'Record doses manually', perKg: null },
]

const INDUCTION_UNITS = { Fentanyl: 'mcg', Ketamine: 'mg', Rocuronium: 'mg' }

// Dose text for one induction drug at a ratio and weight ('' if unknown).
export const inductionDose = (drug, ratio, weightKg) => {
  const option = INDUCTION_RATIOS.find((item) => item.value === ratio)
  const perKg = option?.perKg?.[drug]
  if (perKg === undefined || perKg === null || !weightKg) return ''
  if (perKg === 0) return ''
  const amount = Math.round(perKg * Number(weightKg) * 10) / 10
  return `${amount}${INDUCTION_UNITS[drug]}`
}

// ---------------------------------------------------------------------------
// Presenting-complaint categories (critical care landing grid)
// ---------------------------------------------------------------------------

export const CATEGORIES = [
  { key: 'cardiacArrest', label: 'Cardiac Arrest', description: 'Medical or traumatic arrest, ROSC care', tone: 'red', icon: 'heart', console: 'arrest' },
  { key: 'majorTrauma', label: 'Major Trauma', description: 'MARCH: haemorrhage, airway, chest, circulation, head, hypothermia', tone: 'amber', icon: 'alert', console: 'trauma' },
  { key: 'codeRed', label: 'Major Haemorrhage', description: 'Code Red - blood products and haemorrhage control', tone: 'red', icon: 'droplet', console: 'codeRed' },
  { key: 'neuroTrauma', label: 'Head Injury / TBI', description: 'Neuroprotection and neuro pre-alert (e.g. Code Black)', tone: 'purple', icon: 'brain', console: 'neuro' },
  { key: 'phea', label: 'Emergency Anaesthesia', description: 'PHEA / RSI - checklist, induction, intubation, ventilation', tone: 'blue', icon: 'wind', console: 'phea' },
  { key: 'sedation', label: 'Procedural Sedation', description: 'Sedation for procedures or agitation', tone: 'teal', icon: 'moon', console: 'sedation' },
  { key: 'burns', label: 'Burns', description: 'Thermal, chemical, electrical and inhalation injury', tone: 'coral', icon: 'flame', console: 'burns' },
  { key: 'obstetric', label: 'Obstetric Emergency', description: 'Birth, PPH, eclampsia, shoulder dystocia, breech, cord prolapse', tone: 'purple', icon: 'users', console: 'obstetric' },
  { key: 'newborn', label: 'Newborn Life Support', description: 'Newborn resuscitation timeline and APGAR', tone: 'teal', icon: 'baby', console: 'newborn' },
  { key: 'seizure', label: 'Status Epilepticus', description: 'Seizure timeline, benzodiazepines and second-line drugs', tone: 'blue', icon: 'zap', console: 'seizure' },
  { key: 'medical', label: 'Medical / Other Collapse', description: 'Anaphylaxis, overdose, sepsis, shock and other collapse', tone: 'graphite', icon: 'activity', console: 'medical' },
]

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------
// kind:
//   stamp   - one tap logs it
//   choice  - a second tap picks one of `choices`
//   toggle  - on/off (CPR, REBOA); `onLabel`/`offLabel`
//   drug    - pick from the quick-drug list (or type another)
//   menu    - opens a sheet of other action keys (`items`)
//   console - switches the console to another category (`opens`)
// target:   { section, moduleName, prefill, choiceField, counterField,
//             deferEntry } - the entry the tap creates (deferEntry: only
//             written once the detail form is saved, used for obs)
// detailForm: FORMS key - the "add details" form for the entry
// prompt:   'countdown' - after the tap, offer Add details / Close and
//           auto-close after 5 seconds; 'form' - open the form straight away
// counter:  { label } - numbered taps (Attempt 2, Shock 3)
// clock:    { mode: 'since'|'whileOn'|'sinceFirst', label, warnAfterSeconds }
// levels:   only on consoles of these levels (e.g. CCSV, DSED: Critical Care)
// phaseTo:  arrest console phase change ('arrest' | 'rosc' | 'ended')
// restartsCycle: restarts the 2-minute CPR cycle timer
// review:   case-review trigger label (mirrored on the server)
// chips:    quick annotations offered after the tap (energy, size) -
//           written to the entry's `chipField` and the event detail
// inductionDrugs: (induction) one Medicine entry per drug, dose from the
//           picked ratio and the recorded weight
// implies:  state changes that follow from this tap, applied as extra
//           (implied) toggle events tied to it - undoing the tap undoes them:
//           [{ toggle, on, ifChoice?: [...], ifOn?: bool }]
// metronome: { bpm } - the tile pulses and counts 1-2-3-4 while on
// big:      double-width tile (phone); tabletSpan overrides the width on
//           tablets: 'full' | 'half' | 'third' (default: big = half,
//           otherwise third) so iPad rows hold 2-3 tiles

export const ACTIONS = {
  // ---- Cardiac arrest ------------------------------------------------------
  cpr: {
    key: 'cpr', label: 'CPR', kind: 'toggle', tone: 'red', icon: 'heart', big: true, tabletSpan: 'full',
    onLabel: 'Compressions on', offLabel: 'Hands off',
    hint: 'Tap at every pause and restart - the timeline works out hands-off time and compression fraction.',
    metronome: { bpm: 110 },
    // Compressions mean no pacing capture
    implies: [{ toggle: 'pacing', on: false, ifOn: true }],
  },
  mechanicalCpr: {
    key: 'mechanicalCpr', label: 'Mechanical CPR', kind: 'choice', tone: 'red', icon: 'settings',
    choices: ['LUCAS', 'AutoPulse', 'corpuls cpr', 'Other device'],
    implies: [{ toggle: 'cpr', on: true }],
    target: { section: 'treatment', moduleName: 'Mechanical CPR', choiceField: 'Device' },
    detailForm: 'mechanicalCpr', prompt: 'countdown',
  },
  ccsv: {
    key: 'ccsv', label: 'CCSV', kind: 'stamp', tone: 'blue', icon: 'wind', levels: ['Critical Care'],
    hint: 'Chest compression synchronised ventilation started',
    target: { section: 'treatment', moduleName: 'Ventilation', prefill: { Mode: 'CCSV' } },
    detailForm: 'ventilation', prompt: 'countdown',
  },
  rhythmCheck: {
    key: 'rhythmCheck', label: 'Rhythm Check', kind: 'choice', tone: 'graphite', icon: 'activity', big: true,
    choices: RHYTHMS, restartsCycle: true,
    // Shockable / pulse check = hands off until the shock or decision;
    // non-shockable = compressions straight back on
    implies: [
      { toggle: 'cpr', on: false, ifChoice: ['VF', 'pVT', 'Organised rhythm (check pulse)'] },
      { toggle: 'cpr', on: true, ifChoice: ['PEA', 'Asystole'] },
    ],
    target: { section: 'assess', moduleName: 'ECG', prefill: { 'ECG Type': 'Monitor (arrest)' }, choiceField: 'Arrest Rhythm' },
  },
  shock: {
    key: 'shock', label: 'Shock', kind: 'stamp', tone: 'amber', icon: 'zap', big: true,
    counter: { label: 'Shock' }, restartsCycle: true,
    implies: [{ toggle: 'cpr', on: true }],
    chips: ['120J', '150J', '200J', '300J', '360J'], chipField: 'Energy (J)',
    target: { section: 'treatment', moduleName: 'Defibrillation', prefill: { Type: 'Standard' }, counterField: 'Shock Number' },
    detailForm: 'defibrillation', detailsOptional: true,
  },
  dsed: {
    key: 'dsed', label: 'DSED', kind: 'stamp', tone: 'amber', icon: 'zap', levels: ['Critical Care'],
    hint: 'Double sequential external defibrillation',
    counter: { label: 'Shock' }, restartsCycle: true,
    implies: [{ toggle: 'cpr', on: true }],
    target: { section: 'treatment', moduleName: 'Defibrillation', prefill: { Type: 'Double sequential (DSED)' }, counterField: 'Shock Number' },
    detailForm: 'defibrillation', detailsOptional: true,
  },
  vectorChange: {
    key: 'vectorChange', label: 'Vector Change', kind: 'stamp', tone: 'amber', icon: 'shuffle', levels: ['Critical Care'],
    counter: { label: 'Shock' }, restartsCycle: true,
    implies: [{ toggle: 'cpr', on: true }],
    target: { section: 'treatment', moduleName: 'Defibrillation', prefill: { Type: 'Vector change (VC)', 'Pad Position': 'Anteroposterior' }, counterField: 'Shock Number' },
    detailForm: 'defibrillation', detailsOptional: true,
  },
  pacing: {
    key: 'pacing', label: 'Pacing', kind: 'toggle', tone: 'amber', icon: 'activity',
    onLabel: 'Pacing started', offLabel: 'Pacing stopped',
    target: { section: 'treatment', moduleName: 'Pacing' },
    detailForm: 'pacing', prompt: 'countdown',
  },
  arrestDrug: {
    key: 'arrestDrug', label: 'Drugs', kind: 'drug', tone: 'green', icon: 'droplet', big: true, tabletSpan: 'third',
    clock: { mode: 'since', label: 'Since last drug', warnAfterSeconds: 240 },
    target: { section: 'treatment', moduleName: 'Medicine' },
    detailForm: 'medicine',
  },
  vascularAccess: {
    key: 'vascularAccess', label: 'IV / IO', kind: 'choice', tone: 'teal', icon: 'droplet',
    choices: [
      { value: 'IV', label: 'IV', prefill: { 'Access Type': 'IV' } },
      { value: 'IO humeral', label: 'IO humeral', prefill: { 'Access Type': 'IO', 'Access Location': 'Humeral Head' } },
      { value: 'IO tibial', label: 'IO tibial', prefill: { 'Access Type': 'IO', 'Access Location': 'Proximal Tibia' } },
      { value: 'IO sternal', label: 'IO sternal', prefill: { 'Access Type': 'IO', 'Access Location': 'Sternum' } },
      { value: 'Central / other', label: 'Central / other', prefill: { 'Access Type': 'Other' } },
    ],
    target: { section: 'treatment', moduleName: 'IV/IO' },
    detailForm: 'vascularAccess', prompt: 'countdown',
  },
  airway: {
    key: 'airway', label: 'Airway', kind: 'menu', tone: 'blue', icon: 'wind', big: true, tabletSpan: 'third',
    items: ['bvm', 'opa', 'npa', 'sga', 'intubation', 'tubeConfirmed', 'suction', 'surgicalAirway', 'needleCric'],
  },
  bvm: {
    key: 'bvm', label: 'BVM', kind: 'stamp', tone: 'blue', icon: 'wind',
    target: { section: 'treatment', moduleName: 'Airway Management', prefill: { Type: 'BVM' } },
  },
  opa: {
    key: 'opa', label: 'OPA', kind: 'stamp', tone: 'blue', icon: 'wind',
    chips: ['00', '0', '1', '2', '3', '4'], chipField: 'Size',
    target: { section: 'treatment', moduleName: 'Airway Management', prefill: { Type: 'OPA' } },
  },
  npa: {
    key: 'npa', label: 'NPA', kind: 'stamp', tone: 'blue', icon: 'wind',
    chips: ['6', '7', '8'], chipField: 'Size',
    target: { section: 'treatment', moduleName: 'Airway Management', prefill: { Type: 'NPA' } },
  },
  sga: {
    key: 'sga', label: 'i-gel / SGA', kind: 'stamp', tone: 'blue', icon: 'wind',
    chips: ['1', '1.5', '2', '2.5', '3', '4', '5'], chipField: 'Size',
    target: { section: 'treatment', moduleName: 'Airway Management', prefill: { Type: 'iGel' } },
    detailForm: 'sga', detailsOptional: true,
  },
  intubation: {
    key: 'intubation', label: 'Intubation', kind: 'stamp', tone: 'blue', icon: 'wind', big: true,
    counter: { label: 'Attempt' },
    target: { section: 'treatment', moduleName: 'Airway Management', prefill: { Type: 'ET Intubation' }, counterField: 'Attempt Number' },
    detailForm: 'intubation', prompt: 'countdown',
    hint: 'Each tap is one attempt - attempts are numbered automatically.',
  },
  tubeConfirmed: {
    key: 'tubeConfirmed', label: 'Waveform ETCO2', kind: 'stamp', tone: 'green', icon: 'check',
    hint: 'Tube position confirmed with sustained waveform capnography',
  },
  suction: {
    key: 'suction', label: 'Suction', kind: 'stamp', tone: 'blue', icon: 'wind',
  },
  surgicalAirway: {
    key: 'surgicalAirway', label: 'Surgical Airway', kind: 'stamp', tone: 'red', icon: 'scissors',
    review: 'Surgical airway',
    target: { section: 'treatment', moduleName: 'Airway Management', prefill: { Type: 'Surgical Airway', Technique: 'Scalpel-bougie-tube' } },
    detailForm: 'surgicalAirway', prompt: 'countdown',
  },
  needleCric: {
    key: 'needleCric', label: 'Needle Cric', kind: 'stamp', tone: 'red', icon: 'scissors',
    review: 'Surgical airway',
    target: { section: 'treatment', moduleName: 'Airway Management', prefill: { Type: 'Needle Cricothyroidotomy' } },
    detailForm: 'surgicalAirway', prompt: 'countdown',
  },
  hott: {
    key: 'hott', label: 'TCA (HOTT)', kind: 'menu', tone: 'red', icon: 'alert',
    hint: 'Traumatic cardiac arrest: Hypovolaemia, Oxygenation, Tension, Tamponade',
    items: ['haemorrhageControl', 'bloodProduct', 'pelvicBinder', 'thoracostomy', 'needleDecompression', 'thoracotomy', 'reboa', 'pocus'],
  },
  pocus: {
    key: 'pocus', label: 'POCUS', kind: 'stamp', tone: 'purple', icon: 'monitor',
    target: { section: 'assess', moduleName: 'POCUS' },
    detailForm: 'pocus', prompt: 'countdown',
  },
  rosc: {
    key: 'rosc', label: 'ROSC', kind: 'stamp', tone: 'green', icon: 'heart', big: true, phaseTo: 'rosc',
    hint: 'Switches to the post-ROSC screen',
    implies: [{ toggle: 'cpr', on: false }],
  },
  reArrest: {
    key: 'reArrest', label: 'Re-arrest', kind: 'stamp', tone: 'red', icon: 'heart', big: true, phaseTo: 'arrest',
    restartsCycle: true,
    implies: [{ toggle: 'cpr', on: true }, { toggle: 'pacing', on: false }],
  },
  role: {
    key: 'role', label: 'ROLE', kind: 'stamp', tone: 'graphite', icon: 'x-circle', phaseTo: 'ended', tabletSpan: 'half',
    hint: 'Recognition of life extinct - time of verification',
    implies: [{ toggle: 'cpr', on: false }, { toggle: 'pacing', on: false }],
    detailForm: 'role', prompt: 'form',
    target: { section: 'assess', moduleName: 'Recognition of Life Extinct', deferEntry: true },
  },
  ecpr: {
    key: 'ecpr', label: 'ECPR / Transfer', kind: 'stamp', tone: 'purple', icon: 'truck', levels: ['Critical Care'],
    hint: 'Decision to convey with ongoing CPR (ECPR / cath lab)',
    review: 'Conveyed in arrest / ECPR',
  },
  arrestSummary: {
    key: 'arrestSummary', label: 'Arrest Details', kind: 'stamp', tone: 'graphite', icon: 'file-text',
    hint: 'Utstein data - witnessed, bystander CPR, downtime, first rhythm',
    target: { section: 'treatment', moduleName: 'Cardiac Arrest', deferEntry: true },
    detailForm: 'arrestSummary', prompt: 'form',
  },

  // ---- Post-ROSC -------------------------------------------------------------
  ventilation: {
    key: 'ventilation', label: 'Ventilation', kind: 'stamp', tone: 'blue', icon: 'wind',
    target: { section: 'treatment', moduleName: 'Ventilation' },
    detailForm: 'ventilation', prompt: 'countdown',
  },
  oxygen: {
    key: 'oxygen', label: 'Oxygen', kind: 'choice', tone: 'blue', icon: 'wind',
    choices: ['Non Re-Breather', 'Nasal Cannula', 'Face Mask', 'BVM', 'Ventilator', 'CPAP'],
    target: { section: 'treatment', moduleName: 'Oxygen', choiceField: 'Mask Type' },
  },
  ecg12: {
    key: 'ecg12', label: '12-Lead ECG', kind: 'stamp', tone: 'graphite', icon: 'activity',
    target: { section: 'assess', moduleName: 'ECG', prefill: { 'ECG Type': '12-Lead' } },
    detailForm: 'ecg', prompt: 'countdown',
  },
  obs: {
    key: 'obs', label: 'Obs', kind: 'stamp', tone: 'teal', icon: 'thermometer', big: true,
    target: { section: 'vitals', deferEntry: true },
    detailForm: 'obs', prompt: 'form',
  },
  bloodGas: {
    key: 'bloodGas', label: 'POC / Gas', kind: 'stamp', tone: 'purple', icon: 'droplet',
    target: { section: 'assess', moduleName: 'POC', prefill: { Test: 'Blood Gas' } },
    detailForm: 'bloodGas', prompt: 'countdown',
  },
  infusion: {
    key: 'infusion', label: 'Infusion', kind: 'drug', tone: 'green', icon: 'droplet',
    target: { section: 'treatment', moduleName: 'Infusion' },
    detailForm: 'infusion',
  },
  roscBundle: {
    key: 'roscBundle', label: 'ROSC Checklist', kind: 'stamp', tone: 'green', icon: 'check-square',
    target: { section: 'treatment', moduleName: 'Post-ROSC Care', deferEntry: true },
    detailForm: 'roscBundle', prompt: 'form',
  },
  preAlert: {
    key: 'preAlert', label: 'Pre-alert', kind: 'stamp', tone: 'graphite', icon: 'phone',
    detailForm: 'preAlert', prompt: 'form',
    target: { section: 'treatment', moduleName: 'Pre-alert (ATMIST)', deferEntry: true },
  },
  departScene: { key: 'departScene', label: 'Left Scene', kind: 'stamp', tone: 'graphite', icon: 'truck' },
  atHospital: { key: 'atHospital', label: 'At Hospital', kind: 'stamp', tone: 'graphite', icon: 'home' },
  handover: { key: 'handover', label: 'Handover', kind: 'stamp', tone: 'graphite', icon: 'users' },

  // ---- Common ----------------------------------------------------------------
  drug: {
    key: 'drug', label: 'Medicines', kind: 'drug', tone: 'green', icon: 'droplet', big: true,
    target: { section: 'treatment', moduleName: 'Medicine' },
    detailForm: 'medicine',
  },
  note: {
    key: 'note', label: 'Quick Note', kind: 'stamp', tone: 'graphite', icon: 'edit',
    detailForm: 'note', prompt: 'form',
  },
  patientContact: {
    key: 'patientContact', label: 'At Patient', kind: 'stamp', tone: 'graphite', icon: 'user',
    hint: 'Time the team reached the patient',
  },
  deterioration: {
    key: 'deterioration', label: 'Deterioration', kind: 'choice', tone: 'red', icon: 'trending-down',
    choices: ['Desaturation (SpO2 < 90%)', 'Hypotension (SBP < 90)', 'Bradycardia', 'Arrhythmia', 'Seizure', 'Vomiting / aspiration', 'Agitation', 'Other'],
    review: 'Deterioration logged',
  },

  // ---- Trauma / haemorrhage ---------------------------------------------------
  codeRedDeclared: {
    key: 'codeRedDeclared', label: 'Code Red Declared', kind: 'stamp', tone: 'red', icon: 'alert', big: true,
    clock: { mode: 'sinceFirst', label: 'Code Red' },
  },
  haemorrhageControl: {
    key: 'haemorrhageControl', label: 'Haemorrhage', kind: 'menu', tone: 'red', icon: 'droplet', big: true,
    items: ['tourniquet', 'woundPacking', 'junctionalDevice', 'directPressure', 'pelvicBinder'],
  },
  tourniquet: {
    key: 'tourniquet', label: 'Tourniquet', kind: 'choice', tone: 'red', icon: 'target',
    choices: LIMBS,
    clock: { mode: 'sinceFirst', label: 'Tourniquet time', warnAfterSeconds: 7200 },
    target: { section: 'treatment', moduleName: 'Catastrophic Haemorrhage', prefill: { 'Control Methods': ['Tourniquet'], 'Tourniquet Applied': 'Yes' }, choiceField: 'Affected Area' },
    detailForm: 'haemorrhage', detailsOptional: true,
  },
  woundPacking: {
    key: 'woundPacking', label: 'Wound Packing', kind: 'stamp', tone: 'red', icon: 'layers',
    target: { section: 'treatment', moduleName: 'Catastrophic Haemorrhage', prefill: { 'Control Methods': ['Wound Packing'] } },
    detailForm: 'haemorrhage', prompt: 'countdown',
  },
  junctionalDevice: {
    key: 'junctionalDevice', label: 'Junctional Device', kind: 'stamp', tone: 'red', icon: 'target',
    target: { section: 'treatment', moduleName: 'Catastrophic Haemorrhage', prefill: { 'Control Methods': ['Junctional Tourniquet'] } },
    detailForm: 'haemorrhage', prompt: 'countdown',
  },
  directPressure: {
    key: 'directPressure', label: 'Direct Pressure', kind: 'stamp', tone: 'red', icon: 'target',
    target: { section: 'treatment', moduleName: 'Catastrophic Haemorrhage', prefill: { 'Control Methods': ['Direct Pressure'] } },
  },
  bloodProduct: {
    key: 'bloodProduct', label: 'Blood', kind: 'choice', tone: 'red', icon: 'droplet', big: true,
    choices: ['Red cells', 'Plasma', 'Whole blood', 'Fibrinogen concentrate', 'Platelets'],
    counter: { label: 'Unit' },
    target: { section: 'treatment', moduleName: 'Blood Product', choiceField: 'Product', counterField: 'Unit Number' },
    detailForm: 'bloodProduct', prompt: 'countdown',
    clock: { mode: 'sinceFirst', label: 'First blood' },
  },
  pelvicBinder: {
    key: 'pelvicBinder', label: 'Pelvic Binder', kind: 'stamp', tone: 'amber', icon: 'circle',
    target: { section: 'treatment', moduleName: 'Splinting & Immobilisation', prefill: { 'Affected Area': 'Pelvis', 'Splint Type Used': 'Pelvic Binder' } },
  },
  tractionSplint: {
    key: 'tractionSplint', label: 'Traction Splint', kind: 'choice', tone: 'amber', icon: 'minus',
    choices: ['Left leg', 'Right leg', 'Bilateral'],
    target: { section: 'treatment', moduleName: 'Splinting & Immobilisation', prefill: { 'Splint Type Used': 'Traction Splint' }, choiceField: 'Affected Area' },
    detailForm: 'splint', detailsOptional: true,
  },
  smr: {
    key: 'smr', label: 'Spinal Motion Restriction', kind: 'choice', tone: 'amber', icon: 'minus',
    choices: ['Scoop', 'Vacuum mattress', 'Collar', 'Head blocks', 'Self-extrication', 'Not indicated'],
    target: { section: 'treatment', moduleName: 'Splinting & Immobilisation', choiceField: 'Immobilisation Device' },
  },
  fractureReduction: {
    key: 'fractureReduction', label: 'Fracture / Dislocation Reduction', kind: 'stamp', tone: 'amber', icon: 'refresh',
    target: { section: 'treatment', moduleName: 'Splinting & Immobilisation', prefill: { 'Limb Position': 'Anatomical Position' } },
    detailForm: 'splint', prompt: 'countdown',
  },
  thoracostomy: {
    key: 'thoracostomy', label: 'Thoracostomy', kind: 'choice', tone: 'red', icon: 'scissors',
    choices: SIDES,
    target: { section: 'treatment', moduleName: 'Chest Decompression', prefill: { Technique: 'Simple thoracostomy' }, choiceField: 'Side' },
    detailForm: 'chestDecompression', prompt: 'countdown',
    levels: ['Critical Care'],
  },
  needleDecompression: {
    key: 'needleDecompression', label: 'Needle Decompression', kind: 'choice', tone: 'red', icon: 'scissors',
    choices: SIDES,
    target: { section: 'treatment', moduleName: 'Chest Decompression', prefill: { Technique: 'Needle decompression' }, choiceField: 'Side' },
    detailForm: 'chestDecompression', prompt: 'countdown',
  },
  chestDrain: {
    key: 'chestDrain', label: 'Chest Drain', kind: 'choice', tone: 'red', icon: 'scissors', levels: ['Critical Care'],
    choices: SIDES,
    target: { section: 'treatment', moduleName: 'Chest Decompression', prefill: { Technique: 'Chest drain' }, choiceField: 'Side' },
    detailForm: 'chestDecompression', prompt: 'countdown',
  },
  thoracotomy: {
    key: 'thoracotomy', label: 'Resus Thoracotomy', kind: 'stamp', tone: 'red', icon: 'scissors', levels: ['Critical Care'],
    review: 'Resuscitative thoracotomy',
    target: { section: 'treatment', moduleName: 'Resuscitative Thoracotomy' },
    detailForm: 'thoracotomy', prompt: 'countdown',
  },
  reboa: {
    key: 'reboa', label: 'REBOA', kind: 'toggle', tone: 'red', icon: 'target', levels: ['Critical Care'],
    onLabel: 'Balloon inflated', offLabel: 'Balloon deflated',
    clock: { mode: 'whileOn', label: 'REBOA occlusion', warnAfterSeconds: 1800 },
    review: 'REBOA',
    target: { section: 'treatment', moduleName: 'REBOA' },
    detailForm: 'reboa', prompt: 'countdown',
  },
  fluids: {
    key: 'fluids', label: 'Fluids', kind: 'choice', tone: 'teal', icon: 'droplet',
    choices: ['Sodium Chloride 0.9%', "Hartmann's Solution", 'Glucose 10%', 'Other'],
    target: { section: 'treatment', moduleName: 'IV Fluids', choiceField: 'Fluid Type' },
    detailForm: 'fluids', detailsOptional: true,
  },
  warming: {
    key: 'warming', label: 'Warming', kind: 'choice', tone: 'amber', icon: 'sun',
    choices: ['Blizzard / passive', 'Active warming blanket', 'Fluid / blood warmer', 'Heated vehicle'],
    target: { section: 'treatment', moduleName: 'Warming', choiceField: 'Method' },
  },
  analgesia: {
    key: 'analgesia', label: 'Analgesia', kind: 'drug', tone: 'green', icon: 'droplet',
    target: { section: 'treatment', moduleName: 'Medicine' },
    detailForm: 'medicine',
  },
  antibiotics: {
    key: 'antibiotics', label: 'Antibiotics', kind: 'drug', tone: 'green', icon: 'droplet',
    target: { section: 'treatment', moduleName: 'Medicine' },
    detailForm: 'medicine',
  },
  injuries: {
    key: 'injuries', label: 'Injuries', kind: 'stamp', tone: 'amber', icon: 'clipboard',
    target: { section: 'assess', moduleName: 'Trauma Survey', deferEntry: true },
    detailForm: 'injuries', prompt: 'form',
  },
  surgicalProcedure: {
    key: 'surgicalProcedure', label: 'Other Procedure', kind: 'choice', tone: 'red', icon: 'scissors', levels: ['Critical Care'],
    choices: ['Lateral canthotomy', 'Escharotomy', 'Field amputation', 'Pericardiocentesis', 'Other'],
    review: 'Advanced surgical procedure',
    target: { section: 'treatment', moduleName: 'Surgical Procedure', choiceField: 'Procedure' },
    detailForm: 'procedure', prompt: 'countdown',
  },

  // ---- Neuro -----------------------------------------------------------------
  neuroCheck: {
    key: 'neuroCheck', label: 'GCS / Pupils', kind: 'stamp', tone: 'purple', icon: 'eye', big: true,
    target: { section: 'assess', moduleName: 'Neurological', deferEntry: true },
    detailForm: 'neuroCheck', prompt: 'form',
  },
  headUp: {
    key: 'headUp', label: 'Head-up 30°', kind: 'stamp', tone: 'purple', icon: 'arrow-up',
  },
  osmotherapy: {
    key: 'osmotherapy', label: 'Hypertonic / Mannitol', kind: 'drug', tone: 'green', icon: 'droplet',
    target: { section: 'treatment', moduleName: 'Medicine' },
    detailForm: 'medicine',
  },
  herniation: {
    key: 'herniation', label: 'Herniation Signs', kind: 'choice', tone: 'red', icon: 'alert',
    choices: ['Unilateral fixed dilated pupil', 'Bilateral fixed dilated pupils', 'Extensor posturing', "Cushing's response", 'Fall in GCS ≥ 2'],
  },

  // ---- PHEA ------------------------------------------------------------------
  pheaDecision: {
    key: 'pheaDecision', label: 'Decision to Anaesthetise', kind: 'stamp', tone: 'blue', icon: 'flag', big: true,
    target: { section: 'treatment', moduleName: 'Emergency Anaesthesia' },
    detailForm: 'phea', prompt: 'countdown',
  },
  pheaChecklist: {
    key: 'pheaChecklist', label: 'Checklist', kind: 'stamp', tone: 'blue', icon: 'check-square', big: true,
    target: { section: 'treatment', moduleName: 'PHEA Checklist', deferEntry: true },
    detailForm: 'pheaChecklist', prompt: 'form',
  },
  preoxygenation: {
    key: 'preoxygenation', label: 'Pre-oxygenation', kind: 'toggle', tone: 'blue', icon: 'wind',
    onLabel: 'Started', offLabel: 'Stopped',
    clock: { mode: 'whileOn', label: 'Pre-oxygenation' },
  },
  apnoeicOxygenation: {
    key: 'apnoeicOxygenation', label: 'Apnoeic O₂', kind: 'stamp', tone: 'blue', icon: 'wind',
    hint: 'High-flow nasal oxygen applied',
  },
  induction: {
    key: 'induction', label: 'Induction', kind: 'choice', tone: 'purple', icon: 'droplet', big: true,
    choices: INDUCTION_RATIOS.map((ratio) => ({ value: ratio.value, label: `${ratio.label} · ${ratio.description}` })),
    hint: 'Logs the induction time and adds each induction drug to Treatment for batch / syringe linking.',
    inductionDrugs: ['Fentanyl', 'Ketamine', 'Rocuronium'],
    clock: { mode: 'sinceFirst', label: 'Since induction' },
  },
  postIntubation: {
    key: 'postIntubation', label: 'Post-tube Care', kind: 'menu', tone: 'blue', icon: 'list',
    items: ['tubeConfirmed', 'ventilation', 'ogTube', 'maintenance', 'obs', 'bloodGas'],
  },
  ogTube: {
    key: 'ogTube', label: 'OG / NG Tube', kind: 'stamp', tone: 'blue', icon: 'minus',
    target: { section: 'treatment', moduleName: 'Gastric Tube' },
  },
  maintenance: {
    key: 'maintenance', label: 'Maintenance', kind: 'drug', tone: 'purple', icon: 'droplet',
    hint: 'Ongoing sedation / paralysis (bolus or infusion)',
    target: { section: 'treatment', moduleName: 'Medicine' },
    detailForm: 'medicine',
    clock: { mode: 'since', label: 'Since last maintenance', warnAfterSeconds: 1800 },
  },
  failedAirway: {
    key: 'failedAirway', label: 'Failed Airway', kind: 'menu', tone: 'red', icon: 'alert',
    items: ['sga', 'bvm', 'cico', 'surgicalAirway', 'needleCric'],
  },
  cico: {
    key: 'cico', label: 'CICO Declared', kind: 'stamp', tone: 'red', icon: 'alert',
    hint: "Can't intubate, can't oxygenate",
    review: 'CICO declared',
    clock: { mode: 'sinceFirst', label: 'Since CICO' },
  },

  // ---- Sedation ----------------------------------------------------------------
  sedationStart: {
    key: 'sedationStart', label: 'Sedation Started', kind: 'stamp', tone: 'teal', icon: 'moon', big: true,
    target: { section: 'treatment', moduleName: 'Sedation' },
    detailForm: 'sedation', prompt: 'countdown',
    clock: { mode: 'sinceFirst', label: 'Sedation time' },
  },
  procedure: {
    key: 'procedure', label: 'Procedure', kind: 'choice', tone: 'teal', icon: 'tool',
    choices: ['Fracture reduction', 'Joint relocation', 'Extrication', 'Behavioural disturbance', 'Cardioversion', 'Other'],
  },
  sedationAdverse: {
    key: 'sedationAdverse', label: 'Adverse Event', kind: 'choice', tone: 'red', icon: 'alert',
    choices: ['Apnoea', 'Airway obstruction', 'Laryngospasm', 'Desaturation (SpO2 < 90%)', 'Hypotension', 'Vomiting / aspiration', 'Emergence phenomenon', 'Unplanned intubation'],
    review: 'Sedation adverse event',
  },
  sedationEnd: {
    key: 'sedationEnd', label: 'Recovered', kind: 'stamp', tone: 'green', icon: 'check',
  },

  // ---- Burns -------------------------------------------------------------------
  cooling: {
    key: 'cooling', label: 'Cooling', kind: 'toggle', tone: 'blue', icon: 'droplet', big: true,
    onLabel: 'Cooling started', offLabel: 'Cooling stopped',
    clock: { mode: 'whileOn', label: 'Cooling', warnAfterSeconds: 1200 },
    target: { section: 'treatment', moduleName: 'Burns Care', prefill: { Intervention: 'Cooling' } },
  },
  irrigation: {
    key: 'irrigation', label: 'Irrigation', kind: 'toggle', tone: 'blue', icon: 'droplet',
    onLabel: 'Irrigation started', offLabel: 'Irrigation stopped',
    clock: { mode: 'whileOn', label: 'Irrigation' },
    target: { section: 'treatment', moduleName: 'Burns Care', prefill: { Intervention: 'Chemical irrigation' } },
    detailForm: 'burnsCare', detailsOptional: true,
  },
  burnsAssessment: {
    key: 'burnsAssessment', label: 'TBSA / Depth', kind: 'stamp', tone: 'coral', icon: 'percent', big: true,
    target: { section: 'assess', moduleName: 'Burns Assessment', deferEntry: true },
    detailForm: 'burnsAssessment', prompt: 'form',
  },
  burnsDressing: {
    key: 'burnsDressing', label: 'Cling Film / Dressing', kind: 'stamp', tone: 'coral', icon: 'layers',
    target: { section: 'treatment', moduleName: 'Burns Care', prefill: { Intervention: 'Dressing' } },
  },
  escharotomy: {
    key: 'escharotomy', label: 'Escharotomy', kind: 'stamp', tone: 'red', icon: 'scissors', levels: ['Critical Care'],
    review: 'Escharotomy',
    target: { section: 'treatment', moduleName: 'Surgical Procedure', prefill: { Procedure: 'Escharotomy' } },
    detailForm: 'procedure', prompt: 'countdown',
  },
  inhalationInjury: {
    key: 'inhalationInjury', label: 'Airway Burn Signs', kind: 'choice', tone: 'red', icon: 'alert',
    choices: ['Soot in airway', 'Singed nasal hair', 'Hoarse voice / stridor', 'Facial burns', 'Enclosed space', 'Suspected CO / cyanide'],
    target: { section: 'assess', moduleName: 'Burns Assessment', choiceField: 'Inhalation Injury Markers' },
  },

  // ---- Obstetric -----------------------------------------------------------------
  headToBody: {
    key: 'headToBody', label: 'Head → Body', kind: 'toggle', tone: 'purple', icon: 'clock', big: true,
    onLabel: 'Head delivered', offLabel: 'Body delivered',
    clock: { mode: 'whileOn', label: 'Head-to-body', warnAfterSeconds: 120 },
  },
  babyBorn: {
    key: 'babyBorn', label: 'Baby Born', kind: 'stamp', tone: 'teal', icon: 'baby', big: true,
    clock: { mode: 'sinceFirst', label: 'Since birth' },
    target: { section: 'treatment', moduleName: 'Birth', deferEntry: true },
    detailForm: 'birth', prompt: 'countdown',
  },
  cordClamped: { key: 'cordClamped', label: 'Cord Clamped', kind: 'stamp', tone: 'purple', icon: 'scissors' },
  placenta: { key: 'placenta', label: 'Placenta Delivered', kind: 'stamp', tone: 'purple', icon: 'check' },
  obstetricComplication: {
    key: 'obstetricComplication', label: 'Complication', kind: 'choice', tone: 'red', icon: 'alert', big: true,
    choices: ['Shoulder dystocia', 'Breech', 'Cord prolapse', 'Postpartum haemorrhage', 'Eclampsia', 'Pre-eclampsia', 'Antepartum haemorrhage', 'Uterine inversion', 'Maternal collapse'],
    review: 'Obstetric emergency',
    target: { section: 'assess', moduleName: 'Obstetric Assessment', choiceField: 'Complication' },
    detailForm: 'obstetricComplication', prompt: 'countdown',
  },
  obstetricManoeuvre: {
    key: 'obstetricManoeuvre', label: 'Manoeuvre', kind: 'choice', tone: 'purple', icon: 'refresh',
    choices: ['McRoberts', 'Suprapubic pressure', 'Delivery of posterior arm', 'Internal rotation', 'All fours', 'Knee-chest (cord prolapse)', 'Manual cord elevation', 'Bladder filling', 'Fundal massage', 'Bimanual compression', 'Left uterine displacement'],
    target: { section: 'treatment', moduleName: 'Obstetric Manoeuvre', choiceField: 'Manoeuvre' },
  },
  hysterotomy: {
    key: 'hysterotomy', label: 'Resus Hysterotomy', kind: 'stamp', tone: 'red', icon: 'scissors', levels: ['Critical Care'],
    review: 'Resuscitative hysterotomy',
    target: { section: 'treatment', moduleName: 'Surgical Procedure', prefill: { Procedure: 'Resuscitative hysterotomy' } },
    detailForm: 'procedure', prompt: 'countdown',
  },

  // ---- Newborn ---------------------------------------------------------------------
  dryWrap: { key: 'dryWrap', label: 'Dry / Wrap / Hat', kind: 'stamp', tone: 'teal', icon: 'sun' },
  inflationBreaths: {
    key: 'inflationBreaths', label: 'Inflation Breaths', kind: 'stamp', tone: 'blue', icon: 'wind', big: true,
    counter: { label: 'Set' },
    target: { section: 'treatment', moduleName: 'Newborn Resuscitation', prefill: { Intervention: 'Inflation breaths (5)' }, counterField: 'Set Number' },
  },
  ventilationBreaths: {
    key: 'ventilationBreaths', label: 'Ventilation Breaths', kind: 'stamp', tone: 'blue', icon: 'wind',
    target: { section: 'treatment', moduleName: 'Newborn Resuscitation', prefill: { Intervention: 'Ventilation breaths' } },
  },
  newbornHr: {
    key: 'newbornHr', label: 'Heart Rate', kind: 'choice', tone: 'red', icon: 'heart', big: true,
    choices: ['> 100', '60 - 100', '< 60', 'Absent'],
    clock: { mode: 'since', label: 'Since HR check', warnAfterSeconds: 30 },
    target: { section: 'assess', moduleName: 'Newborn Assessment', choiceField: 'Heart Rate' },
  },
  newbornCompressions: {
    key: 'newbornCompressions', label: 'Compressions 3:1', kind: 'toggle', tone: 'red', icon: 'heart',
    onLabel: 'Compressions on', offLabel: 'Compressions off',
    target: { section: 'treatment', moduleName: 'Newborn Resuscitation', prefill: { Intervention: 'Chest compressions (3:1)' } },
  },
  apgar: {
    key: 'apgar', label: 'APGAR', kind: 'choice', tone: 'teal', icon: 'clipboard',
    choices: ['1 minute', '5 minutes', '10 minutes'],
    target: { section: 'assess', moduleName: 'APGAR', choiceField: 'Time Point' },
    detailForm: 'apgar', prompt: 'form',
  },

  // ---- Seizure ------------------------------------------------------------------------
  seizureOnset: {
    key: 'seizureOnset', label: 'Seizure Onset', kind: 'stamp', tone: 'blue', icon: 'zap', big: true,
    hint: 'Log now and correct the time if the seizure started before arrival',
    clock: { mode: 'sinceFirst', label: 'Since onset' },
  },
  seizureStopped: { key: 'seizureStopped', label: 'Seizure Stopped', kind: 'stamp', tone: 'green', icon: 'check', big: true },
  seizureRecurred: { key: 'seizureRecurred', label: 'Seizure Recurred', kind: 'stamp', tone: 'red', icon: 'zap' },
  seizureDrug: {
    key: 'seizureDrug', label: 'Anticonvulsant', kind: 'drug', tone: 'green', icon: 'droplet', big: true,
    target: { section: 'treatment', moduleName: 'Medicine' },
    detailForm: 'medicine',
    clock: { mode: 'since', label: 'Since last anticonvulsant', warnAfterSeconds: 300 },
  },
  glucose: {
    key: 'glucose', label: 'BM / Glucose', kind: 'stamp', tone: 'teal', icon: 'droplet',
    target: { section: 'vitals', deferEntry: true },
    detailForm: 'glucose', prompt: 'form',
  },

  // ---- Medical -------------------------------------------------------------------------
  nebuliser: {
    key: 'nebuliser', label: 'Nebuliser', kind: 'drug', tone: 'green', icon: 'wind',
    target: { section: 'treatment', moduleName: 'Medicine' },
    detailForm: 'medicine',
  },
  cpap: {
    key: 'cpap', label: 'CPAP / NIV', kind: 'toggle', tone: 'blue', icon: 'wind', onLabel: 'Started', offLabel: 'Stopped',
    target: { section: 'treatment', moduleName: 'Ventilation', prefill: { Mode: 'CPAP / NIV' } },
    detailForm: 'ventilation', detailsOptional: true,
  },
  cardioversion: {
    key: 'cardioversion', label: 'Cardioversion', kind: 'stamp', tone: 'amber', icon: 'zap', levels: ['Critical Care'],
    counter: { label: 'Shock' },
    chips: ['50J', '100J', '120J', '150J', '200J'], chipField: 'Energy (J)',
    target: { section: 'treatment', moduleName: 'Defibrillation', prefill: { Type: 'Synchronised cardioversion' }, counterField: 'Shock Number' },
    detailForm: 'defibrillation', detailsOptional: true,
  },
  sepsisBundle: {
    key: 'sepsisBundle', label: 'Sepsis', kind: 'stamp', tone: 'amber', icon: 'thermometer',
    target: { section: 'assess', moduleName: 'Sepsis Screen', deferEntry: true },
    detailForm: 'sepsis', prompt: 'form',
  },
}

// Older mobile arrest events ('Shock', 'Drug', …) recorded before the live
// catalogue - display-only, never offered on a console.
export const LEGACY_EVENT_LABELS = {
  Started: 'Arrest started',
  Shock: 'Shock',
  'No Shock': 'Rhythm check (no shock)',
  'IV Access': 'IV/IO access',
  Airway: 'Airway management',
  Drug: 'Drug',
  ROSC: 'ROSC',
  'Life Extinct': 'Life extinct',
  arrestStarted: 'Arrest started',
}

// ---------------------------------------------------------------------------
// Consoles (button layouts)
// ---------------------------------------------------------------------------

const COMMON_TAIL = ['obs', 'neuroCheck', 'ecg12', 'pocus', 'bloodGas', 'note']

export const CONSOLES = {
  arrest: {
    label: 'Cardiac Arrest',
    phases: {
      arrest: {
        groups: [
          { title: 'Resuscitation', actions: ['cpr', 'rhythmCheck', 'shock', 'arrestDrug', 'airway', 'vascularAccess'] },
          { title: 'Advanced', actions: ['mechanicalCpr', 'ccsv', 'dsed', 'vectorChange', 'pacing', 'pocus', 'hott', 'ecpr'] },
          { title: 'Outcome', actions: ['rosc', 'role', 'arrestSummary', 'note'] },
        ],
      },
      rosc: {
        banner: 'ROSC - post-resuscitation care',
        groups: [
          { title: 'Status', actions: ['reArrest'] },
          { title: 'Airway & breathing', actions: ['airway', 'ventilation', 'oxygen', 'ccsv'] },
          { title: 'Circulation & monitoring', actions: ['obs', 'ecg12', 'bloodGas', 'pocus', 'infusion', 'arrestDrug', 'fluids'] },
          { title: 'Care', actions: ['roscBundle', 'maintenance', 'preAlert', 'arrestSummary', 'note'] },
        ],
      },
      ended: {
        banner: 'Resuscitation ended',
        groups: [
          { title: 'Status', actions: ['reArrest'] },
          { title: 'Record', actions: ['arrestSummary', 'note'] },
        ],
      },
    },
  },
  trauma: {
    label: 'Major Trauma',
    groups: [
      { title: '<C> Catastrophic haemorrhage', actions: ['haemorrhageControl', 'tourniquet', 'bloodProduct', 'codeRedDeclared'] },
      { title: 'A / B', actions: ['airway', 'oxygen', 'thoracostomy', 'needleDecompression', 'chestDrain'] },
      { title: 'C', actions: ['vascularAccess', 'pelvicBinder', 'fluids', 'reboa', 'thoracotomy'] },
      { title: 'D / E', actions: ['neuroCheck', 'phea', 'warming', 'smr', 'tractionSplint', 'fractureReduction'] },
      { title: 'Drugs', actions: ['drug', 'analgesia', 'antibiotics'] },
      { title: 'Assess & move', actions: ['obs', 'injuries', 'pocus', 'bloodGas', 'surgicalProcedure', 'deterioration', 'preAlert', 'note'] },
    ],
  },
  codeRed: {
    label: 'Code Red',
    groups: [
      { title: 'Code Red', actions: ['codeRedDeclared', 'bloodProduct', 'drug', 'haemorrhageControl'] },
      { title: 'Haemorrhage control', actions: ['tourniquet', 'woundPacking', 'junctionalDevice', 'pelvicBinder', 'reboa', 'thoracotomy'] },
      { title: 'Supportive', actions: ['vascularAccess', 'warming', 'airway', 'thoracostomy', 'needleDecompression'] },
      { title: 'Monitor & move', actions: ['obs', 'bloodGas', 'pocus', 'deterioration', 'preAlert', 'note'] },
    ],
  },
  neuro: {
    label: 'Head Injury / TBI',
    reminders: ['Avoid hypoxia and hypotension', 'Ventilate to your SOP ETCO2 target', 'Record pupils L/R before and after drugs', 'Head-up, neck neutral, ties loose'],
    groups: [
      { title: 'Assess', actions: ['neuroCheck', 'obs', 'herniation', 'glucose'] },
      { title: 'Protect', actions: ['airway', 'phea', 'ventilation', 'headUp', 'osmotherapy', 'drug'] },
      { title: 'Trauma', actions: ['haemorrhageControl', 'vascularAccess', 'smr', 'injuries', 'deterioration', 'preAlert', 'note'] },
    ],
  },
  phea: {
    label: 'Emergency Anaesthesia',
    groups: [
      { title: 'Prepare', actions: ['pheaDecision', 'pheaChecklist', 'preoxygenation', 'apnoeicOxygenation', 'vascularAccess'] },
      { title: 'Induce & intubate', actions: ['induction', 'intubation', 'tubeConfirmed', 'failedAirway', 'airway'] },
      { title: 'After the tube', actions: ['ventilation', 'maintenance', 'ogTube', 'obs', 'bloodGas', 'deterioration'] },
      { title: 'Drugs & notes', actions: ['drug', 'infusion', 'preAlert', 'note'] },
    ],
  },
  sedation: {
    label: 'Procedural Sedation',
    groups: [
      { title: 'Sedation', actions: ['sedationStart', 'drug', 'procedure', 'sedationEnd'] },
      { title: 'Monitor', actions: ['obs', 'vascularAccess', 'oxygen', 'airway'] },
      { title: 'Problems', actions: ['sedationAdverse', 'deterioration', 'note'] },
    ],
  },
  burns: {
    label: 'Burns',
    groups: [
      { title: 'Burn', actions: ['cooling', 'irrigation', 'burnsAssessment', 'burnsDressing'] },
      { title: 'Airway', actions: ['inhalationInjury', 'airway', 'phea', 'oxygen'] },
      { title: 'Circulation & warmth', actions: ['vascularAccess', 'fluids', 'warming', 'escharotomy'] },
      { title: 'Drugs & monitor', actions: ['analgesia', 'drug', 'obs', 'bloodGas', 'preAlert', 'note'] },
    ],
  },
  obstetric: {
    label: 'Obstetric Emergency',
    groups: [
      { title: 'Birth', actions: ['headToBody', 'babyBorn', 'cordClamped', 'placenta'] },
      { title: 'Complications', actions: ['obstetricComplication', 'obstetricManoeuvre', 'hysterotomy', 'deterioration'] },
      { title: 'Treatment', actions: ['drug', 'vascularAccess', 'fluids', 'bloodProduct', 'obs', 'preAlert', 'note'] },
    ],
  },
  newborn: {
    label: 'Newborn Life Support',
    groups: [
      { title: 'First minutes', actions: ['babyBorn', 'dryWrap', 'newbornHr', 'inflationBreaths', 'ventilationBreaths'] },
      { title: 'Escalate', actions: ['newbornCompressions', 'drug', 'vascularAccess', 'airway'] },
      { title: 'Assess', actions: ['apgar', 'obs', 'glucose', 'warming', 'note'] },
    ],
  },
  seizure: {
    label: 'Status Epilepticus',
    groups: [
      { title: 'Seizure', actions: ['seizureOnset', 'seizureStopped', 'seizureRecurred', 'seizureDrug'] },
      { title: 'Support', actions: ['airway', 'oxygen', 'glucose', 'vascularAccess', 'phea'] },
      { title: 'Monitor', actions: ['obs', 'neuroCheck', 'deterioration', 'preAlert', 'note'] },
    ],
  },
  medical: {
    label: 'Medical / Collapse',
    groups: [
      { title: 'Treat', actions: ['drug', 'infusion', 'nebuliser', 'vascularAccess', 'fluids', 'oxygen'] },
      { title: 'Advanced', actions: ['cpap', 'cardioversion', 'pacing', 'phea', 'sedationStart', 'airway'] },
      { title: 'Assess', actions: ['obs', 'ecg12', 'glucose', 'sepsisBundle', 'pocus', 'bloodGas'] },
      { title: 'Move', actions: ['deterioration', 'preAlert', 'note'] },
    ],
  },
}

// 'phea' tile inside other consoles jumps to the PHEA console.
ACTIONS.phea = {
  key: 'phea', label: 'PHEA', kind: 'console', tone: 'blue', icon: 'wind', opens: 'phea',
  hint: 'Open the emergency anaesthesia console',
}

// Scene-time strip shown along the top of every critical care console.
export const SCENE_TIMES = ['patientContact', 'departScene', 'atHospital', 'handover']

export const COMMON_ACTIONS = COMMON_TAIL

// ---------------------------------------------------------------------------
// Detail forms
// ---------------------------------------------------------------------------
// Field types: select | multi | number | text | textarea | yesno | time |
// grade (Cormack-Lehane picker) | staff (pick from crew) | checklist
// (items to confirm; stored as the array of confirmed items).
// `unit` is appended to number values when displayed, not stored.

const performedBy = { key: 'Performed By', type: 'staff' }
const complications = (options) => ({ key: 'Complications', type: 'multi', options })
const notes = { key: 'Notes', type: 'textarea' }

export const FORMS = {
  intubation: {
    title: 'Intubation attempt',
    dops: 'intubation',
    fields: [
      performedBy,
      { key: 'Attempt Number', type: 'number', helper: 'Numbered automatically from your taps' },
      { key: 'Outcome', type: 'select', options: ['Successful', 'Unsuccessful'], required: true },
      { key: 'Laryngoscopy', type: 'select', options: ['Video - Macintosh-type blade', 'Video - hyperangulated blade', 'Direct laryngoscope'] },
      { key: 'Device / Blade', type: 'select', options: ['C-MAC Macintosh 3', 'C-MAC Macintosh 4', 'C-MAC D-Blade', 'McGrath Mac', 'McGrath X-blade', 'Macintosh 3', 'Macintosh 4', 'Miller', 'Other'] },
      { key: 'Grade of View', type: 'grade', helper: 'Cormack-Lehane (Cook modification)' },
      { key: 'POGO (%)', type: 'number', unit: '%', helper: 'Percentage of glottic opening' },
      { key: 'Adjuncts', type: 'multi', options: ['Bougie', 'Stylet', 'Suction (SALAD)', 'Second operator'] },
      { key: 'Optimisation', type: 'multi', options: ['External laryngeal manipulation (BURP)', 'Repositioned / ramped', 'Cricoid released', 'MILS released', 'Blade changed', 'Operator changed'], helper: '30-second drills used' },
      { key: 'Patient Position', type: 'select', options: ['Supine', 'Ramped', 'Head-up', 'Manual in-line stabilisation', 'Lateral', 'Entrapped / non-standard'] },
      { key: 'Location', type: 'select', options: ['On scene - ground', 'On scene - trolley', 'In vehicle', 'In aircraft', 'Other'] },
      { key: 'Tube Size (mm)', type: 'number', unit: 'mm' },
      { key: 'Cuffed', type: 'yesno', default: 'Yes' },
      { key: 'Depth at Teeth (cm)', type: 'number', unit: 'cm' },
      { key: 'Placement Confirmation', type: 'multi', options: ['Sustained waveform capnography (7 waveforms)', 'Visualised through cords', 'Bilateral chest rise', 'Auscultation', 'Misting'], showIf: { key: 'Outcome', in: ['Successful'] } },
      { key: 'Reason Unsuccessful', type: 'select', options: ['Poor view', 'Oesophageal placement', 'Soiled airway', 'Desaturation', 'Equipment failure', 'Anatomy / trauma', 'Other'], showIf: { key: 'Outcome', in: ['Unsuccessful'] } },
      { key: 'Lowest SpO2 (%)', type: 'number', unit: '%' },
      { key: 'Lowest Systolic BP', type: 'number', unit: 'mmHg' },
      complications(['None (confirmed)', 'Desaturation (SpO2 < 90%)', 'Hypotension (SBP < 90 or > 20% fall)', 'Bradycardia', 'Oesophageal intubation - recognised', 'Oesophageal intubation - not recognised', 'Mainstem bronchus intubation', 'New aspiration', 'Dental trauma', 'Cardiac arrest']),
      notes,
    ],
  },
  sga: {
    title: 'Supraglottic airway',
    fields: [performedBy, { key: 'Size', type: 'select', options: ['1', '1.5', '2', '2.5', '3', '4', '5'] }, { key: 'Outcome', type: 'select', options: OUTCOME }, { key: 'Rescue Device', type: 'yesno', helper: 'Inserted after a failed intubation?' }, notes],
  },
  surgicalAirway: {
    title: 'Surgical airway',
    dops: 'surgicalAirway',
    fields: [
      performedBy,
      { key: 'Technique', type: 'select', options: ['Scalpel-bougie-tube', 'Scalpel-finger-bougie', 'Needle cricothyroidotomy', 'Surgical tracheostomy'] },
      { key: 'Indication', type: 'select', options: ['CICO after failed intubation', 'Primary - airway trauma / obstruction', 'Burns / swelling', 'Other'] },
      { key: 'Tube Size (mm)', type: 'number', unit: 'mm' },
      { key: 'Outcome', type: 'select', options: OUTCOME, required: true },
      { key: 'Waveform ETCO2', type: 'yesno' },
      { key: 'Lowest SpO2 (%)', type: 'number', unit: '%' },
      notes,
    ],
  },
  phea: {
    title: 'Emergency anaesthesia',
    dops: 'phea',
    fields: [
      { key: 'Indications', type: 'multi', options: ['Decreased consciousness', 'Hypoxaemia', 'Ineffective ventilation', 'Existing airway obstruction', 'Impending airway obstruction', 'Combative / unmanageable agitation', 'Humanitarian', 'Post-cardiac arrest', 'Anticipated clinical course', 'Other'] },
      { key: 'Pre-induction GCS', type: 'number', helper: 'High pre-induction GCS = awareness risk (NAP5)' },
      { key: 'Difficult Airway Predictors', type: 'multi', options: ['None', 'Previous difficult intubation', 'Reduced neck mobility / collar / MILS', 'Obesity / short neck', 'Mouth opening < 4cm', 'Maxillofacial trauma', 'Blood / vomit in airway', 'Airway burns / swelling'] },
      { key: 'Environment', type: 'multi', options: ['Good', 'Entrapped', 'No 360° access', 'Poor operator position', 'Bright light', 'Darkness', 'Hostile environment', 'In vehicle / aircraft'] },
      { key: 'Checklist', type: 'select', options: ['Used', 'Available - not used', 'None'] },
      { key: 'Anaesthetist', type: 'staff' },
      { key: 'Intubator', type: 'staff' },
      { key: 'Assistant', type: 'staff' },
      { key: 'Location', type: 'select', options: ['Roadside / scene', 'In vehicle', 'Inside property', 'Aircraft', 'ED resus (assisting)', 'Other'] },
      { key: 'Weight (kg)', type: 'number', unit: 'kg' },
      { key: 'Weight Source', type: 'select', options: ['Actual', 'Estimated', 'Length-based', 'Age formula'] },
      { key: 'Induction Ratio', type: 'select', options: INDUCTION_RATIOS.map((ratio) => ratio.value) },
      { key: 'Pre-oxygenation', type: 'multi', options: ['NRB 15 L/min', 'BVM with PEEP valve', 'Nasal cannula (apnoeic oxygenation)', 'NIV / CPAP'] },
      { key: 'Pre-induction SpO2 (%)', type: 'number', unit: '%' },
      { key: 'Pre-induction BP', type: 'text', placeholder: '120/80' },
      { key: 'Total Attempts', type: 'number', helper: 'Filled from the intubation taps when left blank' },
      { key: 'First-pass Success', type: 'yesno' },
      { key: 'Airway Result', type: 'select', options: ['ETT as planned', 'Primary plan failed - rescued (SGA)', 'Primary plan failed - rescued (surgical airway)', 'Airway lost'] },
      { key: 'Lowest SpO2 (%)', type: 'number', unit: '%' },
      { key: 'Lowest Systolic BP', type: 'number', unit: 'mmHg' },
      { key: 'Peri-intubation Complications', type: 'multi', options: ['None (confirmed)', 'Desaturation (SpO2 < 90% or > 10% fall)', 'Hypotension (SBP < 90 or > 20% fall)', 'Bradycardia', 'Cardiac arrest', 'New aspiration', 'Oesophageal intubation', 'Failed airway', 'Awareness concern'] },
      { key: 'Post-intubation ETCO2 (kPa)', type: 'number', unit: 'kPa' },
      { key: 'Ongoing Sedation / Paralysis', type: 'multi', options: ['Ketamine', 'Midazolam', 'Propofol', 'Fentanyl', 'Morphine', 'Rocuronium'] },
      notes,
    ],
  },
  pheaChecklist: {
    title: 'PHEA checklist',
    fields: [
      {
        key: 'Checklist Confirmed',
        type: 'checklist',
        items: [
          'Indication agreed and team briefed (roles assigned: drugs, MILS, airway)',
          'Catastrophic bleeding controlled, TXA given if indicated',
          '360° access to patient, monitoring visible',
          'Oxygen cylinder > 50% with a backup',
          'Pre-oxygenation adequate (SpO2 target reached)',
          'IV/IO access patent ×2 and flushed',
          'SpO2, ECG, NIBP (cycling) and waveform ETCO2 attached',
          'Suction working and at the head',
          'BVM with PEEP valve, OPA/NPA ready',
          'Laryngoscope checked (VL and DL), bougie, tubes ×2, cuff syringe, tie',
          'Rescue SGA and surgical airway kit opened / ready',
          'Drugs drawn up, labelled, doses calculated and cross-checked aloud',
          'Vasopressor immediately available',
          'Post-intubation sedation ready, ventilator set up, HME and catheter mount',
          'Failed airway plan and 30-second drills verbalised',
          'C-spine plan (MILS) if indicated',
        ],
      },
      { key: 'Read By', type: 'staff' },
      { key: 'Concerns Raised', type: 'textarea' },
    ],
  },
  ventilation: {
    title: 'Ventilation',
    fields: [
      { key: 'Mode', type: 'select', options: ['BVM', 'Volume control (VC)', 'Pressure control (PC)', 'SIMV', 'CCSV', 'CPAP / NIV', 'Other'] },
      { key: 'Ventilator', type: 'select', options: ['Hamilton T1', 'Oxylog 3000+', 'Medumat', 'Parapac', 'Other'] },
      { key: 'Tidal Volume (ml)', type: 'number', unit: 'ml' },
      { key: 'Rate', type: 'number', unit: '/min' },
      { key: 'PEEP (cmH2O)', type: 'number', unit: 'cmH2O' },
      { key: 'FiO2 (%)', type: 'number', unit: '%' },
      { key: 'Peak Pressure (cmH2O)', type: 'number', unit: 'cmH2O' },
      { key: 'ETCO2 (kPa)', type: 'number', unit: 'kPa' },
      notes,
    ],
  },
  defibrillation: {
    title: 'Defibrillation',
    fields: [
      { key: 'Type', type: 'select', options: ['Standard', 'Double sequential (DSED)', 'Vector change (VC)', 'Synchronised cardioversion'] },
      { key: 'Energy (J)', type: 'text', placeholder: '150J' },
      { key: 'Pad Position', type: 'select', options: ['Anterolateral', 'Anteroposterior', 'Both (DSED)'] },
      { key: 'Shock Number', type: 'number' },
      { key: 'Rhythm Before', type: 'select', options: RHYTHMS },
      { key: 'Rhythm After', type: 'select', options: RHYTHMS },
      notes,
    ],
  },
  mechanicalCpr: {
    title: 'Mechanical CPR',
    fields: [{ key: 'Device', type: 'select', options: ['LUCAS', 'AutoPulse', 'corpuls cpr', 'Other device'] }, { key: 'Reason', type: 'select', options: ['Prolonged resuscitation', 'Transport with CPR', 'Limited crew', 'ECPR pathway', 'Other'] }, { key: 'Deployment Pause (s)', type: 'number', unit: 's' }, notes],
  },
  pacing: {
    title: 'Pacing',
    fields: [{ key: 'Mode', type: 'select', options: ['Demand', 'Fixed'] }, { key: 'Rate (ppm)', type: 'number', unit: 'ppm' }, { key: 'Current (mA)', type: 'number', unit: 'mA' }, { key: 'Electrical Capture', type: 'yesno' }, { key: 'Mechanical Capture', type: 'yesno' }, notes],
  },
  medicine: {
    title: 'Medicine',
    medicine: true,
    fields: [
      { key: 'Medicine Name', type: 'text', required: true },
      { key: 'Dose', type: 'text', required: true, placeholder: '1mg' },
      { key: 'Route', type: 'select', options: ['IV', 'IO', 'IV/IO', 'IM', 'IN', 'Buccal', 'SC', 'PO', 'PR', 'Nebulised', 'ETT', 'Other'] },
      { key: 'Administered By', type: 'staff' },
      { key: 'Batch', type: 'text' },
      { key: 'Expiry', type: 'text', placeholder: 'MM/YYYY' },
      { key: 'Drawn Up In Advance', type: 'yesno', helper: 'Prepared before this patient (e.g. at start of shift)' },
      { key: 'Witnessed By', type: 'text', helper: 'Required for controlled drugs' },
      notes,
    ],
  },
  infusion: {
    title: 'Infusion',
    medicine: true,
    fields: [
      { key: 'Medicine Name', type: 'text', required: true },
      { key: 'Concentration', type: 'text', placeholder: '1mg in 50ml' },
      { key: 'Rate', type: 'text', placeholder: '5 ml/h or 0.1 mcg/kg/min' },
      { key: 'Route', type: 'select', options: ['IV', 'IO', 'Central'] },
      { key: 'Pump', type: 'text' },
      { key: 'Stopped At', type: 'time' },
      { key: 'Batch', type: 'text' },
      notes,
    ],
  },
  vascularAccess: {
    title: 'Vascular access',
    fields: [
      performedBy,
      { key: 'Access Type', type: 'select', options: ['IV', 'IO', 'Other'] },
      { key: 'Access Size', type: 'select', options: ['14G', '16G', '18G', '20G', '22G', '24G', '15mm', '25mm', '45mm'] },
      { key: 'Access Location', type: 'select', options: ['Right Arm (ACF)', 'Left Arm (ACF)', 'Right Hand/Wrist', 'Left Hand/Wrist', 'External Jugular', 'Humeral Head', 'Proximal Tibia', 'Distal Tibia', 'Distal Femur', 'Sternum'] },
      { key: 'Successful', type: 'yesno', default: 'Yes' },
      { key: 'Attempts', type: 'number' },
      notes,
    ],
  },
  ecg: {
    title: '12-lead ECG',
    fields: [{ key: 'Rhythm', type: 'multi', options: ['Sinus rhythm', 'Sinus tachycardia', 'Sinus bradycardia', 'Atrial fibrillation', 'Atrial flutter', 'SVT', 'VT', 'Heart block', 'Paced'] }, { key: 'ST Changes', type: 'select', options: ['None', 'STEMI', 'ST depression', 'LBBB (new)', 'Other'] }, { key: 'Transmitted', type: 'yesno' }, notes],
  },
  obs: {
    title: 'Observations',
    vitals: true,
    fields: [
      { key: 'heartRate', label: 'Heart rate', type: 'number', unit: 'bpm' },
      { key: 'bpSys', label: 'Systolic BP', type: 'number', unit: 'mmHg' },
      { key: 'bpDia', label: 'Diastolic BP', type: 'number', unit: 'mmHg' },
      { key: 'spo2', label: 'SpO2', type: 'number', unit: '%' },
      { key: 'respRate', label: 'Resp rate', type: 'number', unit: '/min' },
      { key: 'etco2', label: 'ETCO2', type: 'number', unit: 'kPa' },
      { key: 'temp', label: 'Temperature', type: 'number', unit: '°C' },
      { key: 'bgl', label: 'Blood glucose', type: 'number', unit: 'mmol/L' },
      { key: 'gcsEyes', label: 'GCS eyes', type: 'select', options: ['1', '2', '3', '4'] },
      { key: 'gcsVerbal', label: 'GCS verbal', type: 'select', options: ['1', '2', '3', '4', '5'] },
      { key: 'gcsMotor', label: 'GCS motor', type: 'select', options: ['1', '2', '3', '4', '5', '6'] },
      { key: 'painScore', label: 'Pain score', type: 'number' },
    ],
  },
  glucose: {
    title: 'Blood glucose',
    vitals: true,
    fields: [{ key: 'bgl', label: 'Blood glucose', type: 'number', unit: 'mmol/L', required: true }],
  },
  neuroCheck: {
    title: 'GCS and pupils',
    fields: [
      { key: 'GCS Eyes', type: 'select', options: ['1', '2', '3', '4'] },
      { key: 'GCS Verbal', type: 'select', options: ['1', '2', '3', '4', '5', 'T (intubated)'] },
      { key: 'GCS Motor', type: 'select', options: ['1', '2', '3', '4', '5', '6'] },
      { key: 'Pupil Size Left (mm)', type: 'number', unit: 'mm' },
      { key: 'Pupil Size Right (mm)', type: 'number', unit: 'mm' },
      { key: 'Pupil Reaction Left', type: 'select', options: ['Brisk', 'Sluggish', 'Fixed'] },
      { key: 'Pupil Reaction Right', type: 'select', options: ['Brisk', 'Sluggish', 'Fixed'] },
      { key: 'Posturing', type: 'select', options: ['None', 'Flexion (decorticate)', 'Extension (decerebrate)', 'Flaccid'] },
      { key: 'Lateralising Signs', type: 'multi', options: ['None', 'Left weakness', 'Right weakness', 'Facial asymmetry', 'Speech disturbance'] },
      { key: 'Seizure Activity', type: 'yesno' },
      notes,
    ],
  },
  haemorrhage: {
    title: 'Haemorrhage control',
    fields: [
      { key: 'Affected Area', type: 'text' },
      { key: 'Control Methods', type: 'multi', options: ['Direct Pressure', 'Tourniquet', 'Second tourniquet', 'Junctional Tourniquet', 'Wound Packing', 'Hemostatic Dressing', 'Elevation'] },
      { key: 'Haemostatic Agent', type: 'text' },
      { key: 'Bleeding Controlled', type: 'yesno' },
      { key: 'Tourniquet Converted / Removed At', type: 'time' },
      notes,
    ],
  },
  bloodProduct: {
    title: 'Blood product',
    fields: [
      { key: 'Product', type: 'select', options: ['Red cells', 'Plasma', 'Whole blood', 'Fibrinogen concentrate', 'Platelets'] },
      { key: 'Unit Number', type: 'number' },
      { key: 'Donation / Pack Number', type: 'text', required: true },
      { key: 'Blood Group', type: 'select', options: ['O Neg', 'O Pos', 'A', 'B', 'AB', 'Group-specific'] },
      { key: 'Volume (ml)', type: 'number', unit: 'ml' },
      { key: 'Warmer Used', type: 'yesno', default: 'Yes' },
      { key: 'Route', type: 'select', options: ['IV', 'IO', 'Central'] },
      { key: 'Checked By', type: 'staff' },
      { key: 'Reaction', type: 'select', options: ['None observed', 'Suspected reaction'] },
      notes,
    ],
  },
  chestDecompression: {
    title: 'Chest decompression',
    dops: 'thoracostomy',
    fields: [
      performedBy,
      { key: 'Technique', type: 'select', options: ['Simple thoracostomy', 'Finger thoracostomy (re-fingered)', 'Needle decompression', 'Chest drain'] },
      { key: 'Side', type: 'select', options: SIDES },
      { key: 'Site', type: 'select', options: ['4th/5th ICS mid-axillary', '2nd ICS mid-clavicular', 'Other'] },
      { key: 'Findings', type: 'multi', options: ['Air release', 'Blood', 'Lung up', 'Nil'] },
      { key: 'Physiological Response', type: 'select', options: ['Improved', 'No change', 'Worse'] },
      notes,
    ],
  },
  thoracotomy: {
    title: 'Resuscitative thoracotomy',
    dops: 'thoracotomy',
    fields: [
      performedBy,
      { key: 'Indication', type: 'select', options: ['Penetrating TCA', 'Blunt TCA', 'Other'] },
      { key: 'Time From Loss of Output (min)', type: 'number', unit: 'min' },
      { key: 'Findings', type: 'multi', options: ['Tamponade', 'Cardiac wound', 'Great vessel injury', 'Lung injury', 'Empty heart'] },
      { key: 'Interventions', type: 'multi', options: ['Pericardiotomy', 'Clot evacuation', 'Digital occlusion', 'Suture', 'Aortic compression', 'Internal massage'] },
      { key: 'Outcome', type: 'select', options: ['ROSC', 'No ROSC'] },
      notes,
    ],
  },
  reboa: {
    title: 'REBOA',
    dops: 'reboa',
    fields: [performedBy, { key: 'Zone', type: 'select', options: ['Zone I', 'Zone III'] }, { key: 'Access Side', type: 'select', options: ['Left femoral', 'Right femoral'] }, { key: 'Sheath (Fr)', type: 'number', unit: 'Fr' }, { key: 'Balloon Volume (ml)', type: 'number', unit: 'ml' }, { key: 'BP Before', type: 'text' }, { key: 'BP After', type: 'text' }, notes],
  },
  fluids: {
    title: 'IV fluids',
    fields: [{ key: 'Fluid Type', type: 'select', options: ['Sodium Chloride 0.9%', "Hartmann's Solution", 'Glucose 10%', 'Other'] }, { key: 'Dose (ml)', type: 'number', unit: 'ml' }, { key: 'Batch / Expiry', type: 'text' }, notes],
  },
  splint: {
    title: 'Splinting / reduction',
    fields: [{ key: 'Affected Area', type: 'text' }, { key: 'Splint Type Used', type: 'select', options: ['Traction Splint', 'Vacuum Splint', 'Rigid Splint', 'SAM Splint', 'Pelvic Binder', 'Improvised Splint'] }, { key: 'Neurovascular Status After', type: 'select', options: ['Intact', 'Improved', 'Compromised'] }, notes],
  },
  procedure: {
    title: 'Procedure',
    dops: 'procedure',
    fields: [performedBy, { key: 'Procedure', type: 'text' }, { key: 'Indication', type: 'text' }, { key: 'Outcome', type: 'select', options: OUTCOME }, notes],
  },
  pocus: {
    title: 'Point-of-care ultrasound',
    fields: [
      performedBy,
      { key: 'Views', type: 'multi', options: ['Subxiphoid cardiac', 'Lung (pneumothorax)', 'FAST - RUQ', 'FAST - LUQ', 'FAST - pelvis', 'IVC', 'Femoral (REBOA)'] },
      { key: 'Cardiac Activity', type: 'select', options: ['Organised activity', 'Fibrillatory', 'Standstill', 'Not assessed'] },
      { key: 'Pericardial Effusion', type: 'select', options: ['None', 'Present', 'Tamponade'] },
      { key: 'Lung Sliding', type: 'select', options: ['Present bilaterally', 'Absent left', 'Absent right', 'Absent bilaterally', 'Not assessed'] },
      { key: 'Pneumothorax', type: 'select', options: ['None seen', 'Left', 'Right', 'Bilateral'] },
      { key: 'Compression Pause (s)', type: 'number', unit: 's', helper: 'Arrest scans - keep pauses under 10 seconds' },
      { key: 'Free Fluid', type: 'select', options: ['None', 'Present'] },
      notes,
    ],
  },
  bloodGas: {
    title: 'Point-of-care test',
    fields: [
      { key: 'Test', type: 'select', options: ['Blood Gas', 'Lactate', 'Haemoglobin', 'Potassium', 'Other'] },
      { key: 'Sample', type: 'select', options: ['Arterial', 'Venous', 'Capillary'] },
      { key: 'pH', type: 'number' },
      { key: 'pCO2 (kPa)', type: 'number', unit: 'kPa' },
      { key: 'pO2 (kPa)', type: 'number', unit: 'kPa' },
      { key: 'Base Excess (mmol/L)', type: 'number', unit: 'mmol/L' },
      { key: 'Lactate (mmol/L)', type: 'number', unit: 'mmol/L' },
      { key: 'Potassium (mmol/L)', type: 'number', unit: 'mmol/L' },
      { key: 'Haemoglobin (g/L)', type: 'number', unit: 'g/L' },
      notes,
    ],
  },
  roscBundle: {
    title: 'Post-ROSC checklist',
    fields: [
      {
        key: 'Checklist Confirmed',
        type: 'checklist',
        items: [
          'Airway secured, waveform ETCO2 present',
          'Oxygen titrated to SpO2 94-98%',
          'Ventilation for normocapnia',
          'Blood pressure target set and treated',
          '12-lead ECG recorded and reviewed',
          'Blood glucose checked',
          'Temperature checked - avoid hyperthermia',
          'Sedation / analgesia considered',
          'Cause considered (4 Hs and 4 Ts)',
          'Destination agreed (PPCI / cardiac arrest centre)',
        ],
      },
      notes,
    ],
  },
  role: {
    title: 'Recognition of life extinct',
    fields: [
      { key: 'Verified By', type: 'staff', required: true },
      { key: 'Basis', type: 'select', options: ['Resuscitation stopped (futility / decision to terminate)', 'Condition unequivocally associated with death', 'DNACPR / ReSPECT / advance decision', 'Other'] },
      { key: 'Confirmation', type: 'checklist', items: ['No central pulse for 1 minute', 'No heart sounds for 1 minute', 'No breath sounds for 1 minute', 'Fixed dilated pupils', 'No response to central stimulus', 'Asystole on ECG for > 30 seconds'] },
      { key: 'Decision Discussed With', type: 'text', helper: 'e.g. HEMS consultant, clinical advice line' },
      { key: 'Police / Coroner Informed', type: 'yesno' },
      { key: 'Family Present', type: 'yesno' },
      notes,
    ],
  },
  arrestSummary: {
    title: 'Arrest details (Utstein)',
    fields: [
      { key: 'Arrest Type', type: 'select', options: ['Witnessed', 'Unwitnessed'] },
      { key: 'Witnessed By', type: 'select', options: ['Bystander', 'Ambulance crew', 'HEMS team', 'Not witnessed'] },
      { key: 'Aetiology', type: 'select', options: ['Presumed cardiac', 'Trauma', 'Drowning', 'Overdose', 'Asphyxia', 'Electrocution', 'Other medical'] },
      { key: 'Location', type: 'select', options: ['Home', 'Public place', 'Workplace', 'Road', 'Care home', 'Other'] },
      { key: 'Time of Collapse', type: 'time' },
      { key: 'Bystander CPR', type: 'yesno' },
      { key: 'Dispatcher-assisted CPR', type: 'yesno' },
      { key: 'First CPR By', type: 'select', options: ['Bystander', 'First responder', 'Ambulance crew', 'HEMS team'] },
      { key: 'Public Access Defibrillator Used', type: 'yesno' },
      { key: 'First Defibrillation By', type: 'select', options: ['Public access AED', 'First responder', 'Ambulance crew', 'HEMS team', 'Not shocked'] },
      { key: 'Estimated Downtime (min)', type: 'number', unit: 'min' },
      { key: 'Initial Rhythm', type: 'select', options: ['VF', 'pVT', 'PEA', 'Asystole', 'Unknown'] },
      { key: 'Outcome At Scene', type: 'select', options: ['Sustained ROSC', 'Transported with CPR', 'ROLE / resuscitation stopped'] },
      notes,
    ],
  },
  preAlert: {
    title: 'Pre-alert (ATMIST)',
    fields: [
      { key: 'Receiving Unit', type: 'text' },
      { key: 'Age / Sex', type: 'text' },
      { key: 'Time of Incident', type: 'text' },
      { key: 'Mechanism', type: 'textarea' },
      { key: 'Injuries / Illness', type: 'textarea' },
      { key: 'Signs', type: 'textarea' },
      { key: 'Treatment', type: 'textarea' },
      { key: 'ETA', type: 'text' },
      { key: 'Requests', type: 'multi', options: ['Trauma team', 'Code Red / MHP', 'Cardiac arrest centre', 'PPCI', 'Neurosurgery', 'Obstetrics', 'Paediatrics', 'Burns'] },
    ],
  },
  injuries: {
    title: 'Injuries',
    fields: [
      { key: 'Mechanism', type: 'multi', options: ['RTC - driver', 'RTC - passenger', 'Pedestrian', 'Motorcycle', 'Cyclist', 'Fall > 2m', 'Fall < 2m', 'Stabbing', 'Gunshot', 'Assault', 'Crush', 'Agricultural', 'Equestrian', 'Blast', 'Other'] },
      { key: 'Injuries Suspected', type: 'multi', options: ['Head', 'Face', 'Neck / C-spine', 'Chest', 'Abdomen', 'Pelvis', 'Spine', 'Upper limb', 'Lower limb', 'Burns', 'Degloving', 'Amputation'] },
      { key: 'Time of Injury', type: 'time', helper: 'Used for the TXA-within-3-hours check' },
      { key: 'Entrapped', type: 'yesno' },
      { key: 'Extrication Time (min)', type: 'number', unit: 'min', showIf: { key: 'Entrapped', in: ['Yes'] } },
      { key: 'Description', type: 'textarea' },
    ],
  },
  burnsAssessment: {
    title: 'Burns assessment',
    fields: [
      { key: 'TBSA (%)', type: 'number', unit: '%', required: true, helper: 'Partial and full thickness only - not erythema' },
      { key: 'Cooling Started (min after injury)', type: 'number', unit: 'min' },
      { key: 'Method', type: 'select', options: ['Lund & Browder', 'Rule of nines', 'Palmar surface (1%)', 'Mersey Burns app'] },
      { key: 'Depth', type: 'multi', options: ['Superficial (erythema - not counted)', 'Partial thickness - superficial', 'Partial thickness - deep', 'Full thickness'] },
      { key: 'Areas', type: 'multi', options: ['Face', 'Neck', 'Chest', 'Back', 'Abdomen', 'Perineum', 'Hands', 'Feet', 'Arms', 'Legs'] },
      { key: 'Circumferential', type: 'yesno' },
      { key: 'Mechanism', type: 'select', options: ['Flame', 'Scald', 'Contact', 'Chemical', 'Electrical', 'Flash', 'Friction'] },
      { key: 'Inhalation Injury Markers', type: 'multi', options: ['Soot in airway', 'Singed nasal hair', 'Hoarse voice / stridor', 'Facial burns', 'Enclosed space', 'Suspected CO / cyanide'] },
      { key: 'Agent (chemical)', type: 'text', showIf: { key: 'Mechanism', in: ['Chemical'] } },
      { key: 'Voltage (electrical)', type: 'select', options: ['Low (< 1000V)', 'High (≥ 1000V)', 'Lightning'], showIf: { key: 'Mechanism', in: ['Electrical'] } },
      notes,
    ],
  },
  burnsCare: {
    title: 'Burns care',
    fields: [{ key: 'Intervention', type: 'select', options: ['Cooling', 'Chemical irrigation', 'Dressing'] }, { key: 'Fluid Used', type: 'text' }, { key: 'Volume (L)', type: 'number', unit: 'L' }, notes],
  },
  obstetricComplication: {
    title: 'Obstetric complication',
    fields: [
      { key: 'Complication', type: 'select', options: ['Shoulder dystocia', 'Breech', 'Cord prolapse', 'Postpartum haemorrhage', 'Eclampsia', 'Pre-eclampsia', 'Antepartum haemorrhage', 'Uterine inversion', 'Maternal collapse'] },
      { key: 'Gestation (weeks)', type: 'number', unit: 'weeks' },
      { key: 'Estimated Blood Loss (ml)', type: 'number', unit: 'ml' },
      { key: 'Manoeuvres Used', type: 'multi', options: ['McRoberts', 'Suprapubic pressure', 'Delivery of posterior arm', 'Internal rotation', 'All fours', 'Knee-chest', 'Manual cord elevation', 'Bladder filling', 'Fundal massage', 'Bimanual compression'] },
      { key: 'Anterior Shoulder', type: 'select', options: ['Left', 'Right'], showIf: { key: 'Complication', in: ['Shoulder dystocia'] } },
      { key: 'Seizure Duration (min)', type: 'number', unit: 'min', showIf: { key: 'Complication', in: ['Eclampsia'] } },
      { key: 'Midwife / Obstetric Support Contacted', type: 'yesno' },
      notes,
    ],
  },
  birth: {
    title: 'Birth',
    fields: [
      { key: 'Presentation', type: 'select', options: ['Cephalic', 'Breech', 'Other'] },
      { key: 'Sex', type: 'select', options: ['Female', 'Male', 'Not determined'] },
      { key: 'Cord Clamping Delay (s)', type: 'number', unit: 's' },
      { key: 'Placenta Complete', type: 'yesno' },
      { key: 'Needed Resuscitation', type: 'yesno', helper: 'Open the Newborn Life Support console if yes' },
      notes,
    ],
  },
  apgar: {
    title: 'APGAR',
    fields: [
      { key: 'Time Point', type: 'select', options: ['1 minute', '5 minutes', '10 minutes'] },
      { key: 'Appearance', type: 'select', options: ['0 - blue / pale', '1 - body pink, extremities blue', '2 - completely pink'] },
      { key: 'Pulse', type: 'select', options: ['0 - absent', '1 - < 100', '2 - > 100'] },
      { key: 'Grimace', type: 'select', options: ['0 - no response', '1 - grimace', '2 - cry / cough'] },
      { key: 'Activity', type: 'select', options: ['0 - limp', '1 - some flexion', '2 - active'] },
      { key: 'Respiration', type: 'select', options: ['0 - absent', '1 - slow / irregular', '2 - good / crying'] },
    ],
  },
  sedation: {
    title: 'Procedural sedation',
    dops: 'sedation',
    fields: [
      { key: 'Indication', type: 'select', options: ['Procedural (e.g. dislocation, fracture manipulation)', 'Agitation (e.g. acute behavioural disturbance)', 'Extrication', 'Cardioversion', 'Pain relief (inadequate response to standard analgesia)', 'Other'] },
      { key: 'Agents Used', type: 'multi', options: ['Ketamine', 'Midazolam', 'Propofol', 'Fentanyl', 'Morphine', 'Other'] },
      { key: 'Estimated Weight', type: 'number', unit: 'kg' },
      { key: 'Consent Obtained', type: 'select', options: ['Yes', 'No', 'Unable'] },
      { key: 'Pre-Oxygenation Performed', type: 'select', options: ['Yes', 'No', 'Unable'] },
      { key: 'Target Depth', type: 'select', options: ['Minimal', 'Moderate', 'Dissociative', 'Deep'] },
      { key: 'Procedure Successful', type: 'yesno' },
      { key: 'Adverse Reaction', type: 'multi', options: ['None', 'Apnoea', 'Airway Obstruction', 'Laryngospasm', 'Desaturation (SpO2 < 90%)', 'Hypotension', 'Vomiting / Aspiration', 'Emergence Phenomenon', 'Unplanned intubation'] },
      { key: 'Intervention Required', type: 'multi', options: ['None', 'Airway repositioning', 'Supplemental oxygen', 'BVM ventilation', 'Reversal agent', 'Fluid / pressor', 'Intubation', 'CPR'] },
      { key: 'Adverse Event Severity', type: 'select', options: ['None', 'Minimal', 'Minor', 'Moderate', 'Sentinel'], helper: 'World SIVA adverse sedation event tool' },
      { key: 'Recovery', type: 'select', options: ['Full recovery on scene', 'Conveyed sedated', 'Escalated to PHEA'] },
      notes,
    ],
  },
  sepsis: {
    title: 'Sepsis screen',
    fields: [{ key: 'Suspected Source', type: 'select', options: ['Chest', 'Urine', 'Abdomen', 'Skin / soft tissue', 'Meningitis', 'Unknown', 'Other'] }, { key: 'Red Flags', type: 'multi', options: ['NEWS2 ≥ 7', 'Lactate ≥ 2', 'Systolic BP < 90', 'New confusion', 'Non-blanching rash', 'Mottled / ashen'] }, { key: 'Antibiotics Given', type: 'yesno' }, notes],
  },
  note: {
    title: 'Quick note',
    fields: [{ key: 'Note', type: 'textarea', required: true }],
    eventOnly: true,
  },
}

// Procedures that feed the clinician logbook and can have a DOPS requested
// against them. `match` picks treatment entries by moduleName (+ Type).
export const LOGBOOK_PROCEDURES = [
  { key: 'intubation', label: 'Intubation', match: { moduleName: 'Airway Management', field: 'Type', values: ['ET Intubation'] } },
  { key: 'phea', label: 'Emergency anaesthesia', match: { moduleName: 'Emergency Anaesthesia' } },
  { key: 'surgicalAirway', label: 'Surgical airway', match: { moduleName: 'Airway Management', field: 'Type', values: ['Surgical Airway', 'Needle Cricothyroidotomy'] } },
  { key: 'sga', label: 'Supraglottic airway', match: { moduleName: 'Airway Management', field: 'Type', values: ['iGel', 'LMA'] } },
  { key: 'thoracostomy', label: 'Thoracostomy / chest decompression', match: { moduleName: 'Chest Decompression' } },
  { key: 'thoracotomy', label: 'Resuscitative thoracotomy', match: { moduleName: 'Resuscitative Thoracotomy' } },
  { key: 'reboa', label: 'REBOA', match: { moduleName: 'REBOA' } },
  { key: 'bloodProduct', label: 'Blood product transfusion', match: { moduleName: 'Blood Product' } },
  { key: 'sedation', label: 'Procedural sedation', match: { moduleName: 'Sedation' } },
  { key: 'io', label: 'Intraosseous access', match: { moduleName: 'IV/IO', field: 'Access Type', values: ['IO'] } },
  { key: 'pocus', label: 'POCUS', match: { moduleName: 'POCUS', section: 'assess' } },
  { key: 'procedure', label: 'Other surgical procedure', match: { moduleName: 'Surgical Procedure' } },
]

// DOPS (directly observed procedural skills). Generic UK WPBA domains -
// services can swap in their own templates later.
export const DOPS_DOMAINS = [
  'Understands indications, contraindications and anatomy',
  'Explanation / consent where possible',
  'Preparation and equipment check',
  'Team briefing and role allocation',
  'Analgesia / anaesthesia / sedation appropriate',
  'Technical ability',
  'Asepsis and safe sharps handling',
  'Recognises complications and seeks help appropriately',
  'Post-procedure management and documentation',
  'Communication and team-working',
  'Professionalism',
]
export const DOPS_RATINGS = ['Below expectations', 'Borderline', 'Meets expectations', 'Above expectations', 'Not observed']
export const DOPS_OVERALL = ['Needs further supervised practice', 'Competent with indirect supervision', 'Competent to perform independently']

// Hot debrief (STOP5: Summarise, Things that went well, Opportunities to
// improve, Points to action) plus a wellbeing check.
export const DEBRIEF_FIELDS = [
  { key: 'summary', label: 'Summarise the case', type: 'textarea' },
  { key: 'wentWell', label: 'Things that went well', type: 'textarea' },
  { key: 'improve', label: 'Opportunities to improve', type: 'textarea' },
  { key: 'actions', label: 'Points to action (and who)', type: 'textarea' },
  { key: 'equipmentIssues', label: 'Equipment or drug issues', type: 'textarea' },
  { key: 'attendees', label: 'Who took part', type: 'text' },
  { key: 'wellbeingConcerns', label: 'Anyone need wellbeing support?', type: 'yesno' },
  { key: 'flagForReview', label: 'Flag for clinical case review', type: 'yesno' },
]

// Detail forms that can be added by hand from the Treatment / Assess
// screens on a critical care record (not only from a console tap).
export const MANUAL_FORMS = {
  treatment: [
    { form: 'intubation', moduleName: 'Airway Management', prefill: { Type: 'ET Intubation' }, label: 'Intubation attempt', liveAction: 'intubation' },
    { form: 'phea', moduleName: 'Emergency Anaesthesia', label: 'Emergency anaesthesia', liveAction: 'pheaDecision' },
    { form: 'surgicalAirway', moduleName: 'Airway Management', prefill: { Type: 'Surgical Airway' }, label: 'Surgical airway', liveAction: 'surgicalAirway' },
    { form: 'ventilation', moduleName: 'Ventilation', label: 'Ventilation', liveAction: 'ventilation' },
    { form: 'defibrillation', moduleName: 'Defibrillation', label: 'Defibrillation / cardioversion', liveAction: 'shock' },
    { form: 'bloodProduct', moduleName: 'Blood Product', label: 'Blood product', liveAction: 'bloodProduct' },
    { form: 'chestDecompression', moduleName: 'Chest Decompression', label: 'Chest decompression', liveAction: 'thoracostomy' },
    { form: 'thoracotomy', moduleName: 'Resuscitative Thoracotomy', label: 'Resuscitative thoracotomy', liveAction: 'thoracotomy' },
    { form: 'reboa', moduleName: 'REBOA', label: 'REBOA', liveAction: 'reboa' },
    { form: 'infusion', moduleName: 'Infusion', label: 'Infusion', liveAction: 'infusion' },
    { form: 'pacing', moduleName: 'Pacing', label: 'Pacing', liveAction: 'pacing' },
    { form: 'procedure', moduleName: 'Surgical Procedure', label: 'Other surgical procedure', liveAction: 'surgicalProcedure' },
    { form: 'roscBundle', moduleName: 'Post-ROSC Care', label: 'Post-ROSC checklist', liveAction: 'roscBundle' },
    { form: 'arrestSummary', moduleName: 'Cardiac Arrest', label: 'Arrest details (Utstein)', liveAction: 'arrestSummary' },
    { form: 'preAlert', moduleName: 'Pre-alert (ATMIST)', label: 'Pre-alert (ATMIST)', liveAction: 'preAlert' },
  ],
  assess: [
    { form: 'neuroCheck', moduleName: 'Neurological', label: 'GCS and pupils', liveAction: 'neuroCheck' },
    { form: 'pocus', moduleName: 'POCUS', label: 'POCUS', liveAction: 'pocus' },
    { form: 'burnsAssessment', moduleName: 'Burns Assessment', label: 'Burns assessment', liveAction: 'burnsAssessment' },
    { form: 'injuries', moduleName: 'Trauma Survey', label: 'Injuries', liveAction: 'injuries' },
    { form: 'apgar', moduleName: 'APGAR', label: 'APGAR', liveAction: 'apgar' },
  ],
}

// Case-review triggers evaluated on submission (server mirror:
// server/src/helpers/epcr/criticalCareReview.js). Shown on the Review screen
// so crews know a case will be reviewed.
export const REVIEW_TRIGGER_NOTE =
  'Emergency anaesthesia, failed or surgical airways, more than one intubation attempt, peri-intubation hypoxia or hypotension, thoracotomy, REBOA, hysterotomy and other surgical procedures, sedation adverse events, deteriorations logged on the console, obstetric emergencies, conveyance in arrest and cases flagged in the hot debrief are sent for clinical case review when the record is submitted.'
